import axiosInstance from '../../services/axiosConfig';

/**
 * Campaign Advertising Service
 * Handles all API calls related to campaign advertising requirements
 */

export const campaignAdvertisingService = {
  /**
   * Get all campaign advertising requirements for a company
   * @param {Object} params - Query parameters (hireType, status, page, limit)
   * @returns {Promise}
   */
  getAllCampaignAdvertisings: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/company/campaigns/advertising/all', { params });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to fetch campaign advertisings';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get all campaign advertising requirements for a specific campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} params - Query parameters (hireType, status)
   * @returns {Promise}
   */
  getCampaignAdvertisings: async (campaignId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/advertising`, { params });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to fetch campaign advertisings';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a single campaign advertising requirement by ID
   * @param {string} campaignId - Campaign ID
   * @param {string} advertisingId - Advertising requirement ID
   * @returns {Promise}
   */
  getCampaignAdvertisingById: async (campaignId, advertisingId) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/advertising/${advertisingId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to fetch campaign advertising';
      throw new Error(errorMessage);
    }
  },

  /**
   * Create a new campaign advertising requirement
   * @param {string} campaignId - Campaign ID
   * @param {Object} data - Campaign advertising data
   * @returns {Promise}
   */
  createCampaignAdvertising: async (campaignId, data) => {
    try {
      const response = await axiosInstance.post(`/company/campaigns/${campaignId}/advertising`, data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to create campaign advertising';
      throw new Error(errorMessage);
    }
  },

  /**
   * Update a campaign advertising requirement
   * @param {string} campaignId - Campaign ID
   * @param {string} advertisingId - Advertising requirement ID
   * @param {Object} data - Updated data
   * @returns {Promise}
   */
  updateCampaignAdvertising: async (campaignId, advertisingId, data) => {
    try {
      const response = await axiosInstance.put(
        `/company/campaigns/${campaignId}/advertising/${advertisingId}`,
        data
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to update campaign advertising';
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a campaign advertising requirement
   * @param {string} campaignId - Campaign ID
   * @param {string} advertisingId - Advertising requirement ID
   * @returns {Promise}
   */
  deleteCampaignAdvertising: async (campaignId, advertisingId) => {
    try {
      const response = await axiosInstance.delete(
        `/company/campaigns/${campaignId}/advertising/${advertisingId}`
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to delete campaign advertising';
      throw new Error(errorMessage);
    }
  },

  /**
   * Update status of a campaign advertising requirement
   * @param {string} campaignId - Campaign ID
   * @param {string} advertisingId - Advertising requirement ID
   * @param {string} status - New status
   * @returns {Promise}
   */
  updateCampaignAdvertisingStatus: async (campaignId, advertisingId, status) => {
    try {
      const response = await axiosInstance.patch(
        `/company/campaigns/${campaignId}/advertising/${advertisingId}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to update campaign advertising status';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get published campaign advertisings (public endpoint - no auth required)
   * @param {Object} params - Query parameters (hireType, page, limit)
   * @returns {Promise}
   */
  getPublishedCampaignAdvertisings: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/company/campaigns/advertising/public', {
        params,
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           'Failed to fetch published campaign advertisings';
      throw new Error(errorMessage);
    }
  },

  toggleAdvertisingStatus: async (campaignId, advertisingId) => {
    try {
      const res = await axiosInstance.patch(
        `/company/campaigns/${campaignId}/advertising/${advertisingId}/toggle`
      );
      return res.data;
    } catch (error) {
      throw error.response?.data || { message: "Toggle failed" };
    }
  },
  
};

export default campaignAdvertisingService;

